import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { trackPurchase } from "@/lib/pixel";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          // Fire Meta Pixel Purchase event once, with the actual amount charged by Stripe.
          trackPurchase(Number(data.amount) || 0, (data.currency || "EUR").toUpperCase());
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch {
        /* keep polling */
      }
      polls += 1;
      if (polls >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6" data-testid="payment-success-page">
      <Logo className="mb-12" />
      {status === "checking" && (
        <div className="flex flex-col items-center text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#D4AF37]" />
          <p className="mt-6 font-serif text-3xl font-light text-white">Confermiamo il pagamento…</p>
          <p className="mt-2 text-white/40">Un istante, stiamo verificando con Stripe.</p>
        </div>
      )}
      {status === "paid" && (
        <div className="flex flex-col items-center text-center">
          <CheckCircle2 className="h-14 w-14 text-emerald-400" />
          <p className="mt-6 font-serif text-4xl font-light text-white">Pagamento riuscito!</p>
          <p className="mt-2 max-w-md text-white/50">Il tuo pacchetto è attivo. Il team può ora procedere con il tuo libro.</p>
          <Button onClick={() => navigate("/dashboard")} data-testid="payment-back-dashboard" className="mt-8 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
            Torna alla dashboard
          </Button>
        </div>
      )}
      {(status === "timeout" || status === "error") && (
        <div className="flex flex-col items-center text-center">
          <XCircle className="h-14 w-14 text-white/40" />
          <p className="mt-6 font-serif text-3xl font-light text-white">
            {status === "timeout" ? "Verifica in corso" : "Qualcosa è andato storto"}
          </p>
          <p className="mt-2 max-w-md text-white/50">
            {status === "timeout"
              ? "Il pagamento potrebbe richiedere ancora qualche istante. Controlla la dashboard tra poco."
              : "Non siamo riusciti a confermare il pagamento. Riprova dalla dashboard."}
          </p>
          <Button onClick={() => navigate("/dashboard")} className="mt-8 rounded-full bg-white/5 text-white hover:bg-white/10">
            Torna alla dashboard
          </Button>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6" data-testid="payment-cancel-page">
      <Logo className="mb-12" />
      <XCircle className="h-14 w-14 text-white/40" />
      <p className="mt-6 font-serif text-3xl font-light text-white">Pagamento annullato</p>
      <p className="mt-2 max-w-md text-center text-white/50">Nessun addebito effettuato. Puoi completare l'acquisto quando vuoi.</p>
      <Button onClick={() => navigate("/dashboard")} className="mt-8 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
        Torna alla dashboard
      </Button>
    </div>
  );
}
